import request from '../../library/request';
import store from '../../store';
import setGlobalState from '../setGlobalState';

// Retry the initial ticket fetch on transient failures (5xx / network errors).
// 3 total attempts with short backoff + jitter; 4xx (bad token etc.) fails immediately.
const GET_ETICKET_ATTEMPTS = 3;
const RETRY_DELAYS_MS = [500, 1500];

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const getETicket = async (token: string) => {
    let res;
    for(let attempt = 1; attempt <= GET_ETICKET_ATTEMPTS; attempt++) {
        try {
            // timeout: a request stuck on a dead connection (stalled QUIC/TCP on mobile
            // networks) never errors on its own. Proven on iPhone: while the original
            // request hung for 100s+, a fresh fetch to the same API returned 200 in ~1s.
            // Aborting lets the retry below reopen on a fresh connection automatically.
            res = await request('eTicket.getETicket', 'get', `/api/eTicket/${token}`, undefined, {handleError: true, noRafStatusUpdate: true, timeout: 20000});
            break;
        } catch (e) {
            const status = (e as {response?: {status?: number}})?.response?.status;
            const isRetryable = !status || status >= 500;
            if(!isRetryable || attempt === GET_ETICKET_ATTEMPTS) {
                console.error(`eTicket.getETicket failed (status ${status ?? 'network'}, ${attempt} attempts)`);
                break;
            }
            await wait(RETRY_DELAYS_MS[attempt - 1] + Math.random() * 300);
        }
    }
    // The eTicket page is a standalone route, so the admin event slice is empty and every
    // date helper falls back to DEFAULT_TIME_ZONE. Feeding the event's timezone into that
    // fallback (before the data renders) makes all times on the page show the event's local time.
    if(res?.event?.timeZone) {
        setGlobalState('event.timeZone', res.event.timeZone);
    }
    setGlobalState('eTicket', {oid: res?.order?.oid ?? null, data: res, error: null});
    return res;
};

export const getWalletLink = async (token: string): Promise<string | undefined> => {
    return await request('eTicket.wallet', 'get', `/api/eTicket/${token}/wallet`);
};

export const getETicketScanStatus = async (token: string, qid: number) => {
    return request('eTicket.getETicketScanStatus', 'get', `/api/eTicket/${token}/scan/${qid}`);
};

export const setETicketTicketScanned = (qid: number, scanTs?: number) => {
    const {data} = store.getState().eTicket;
    if(!data?.tickets) {
        return;
    }

    const index = data.tickets.findIndex(ticket => ticket.qid === qid);
    if(index === -1) {
        return;
    }

    setGlobalState(`eTicket.data.tickets[${index}]`, {...data.tickets[index], scanned: true, scanTs});
};
